/**
 * Reordering the "Parts of the event" editor's rows (task #934).
 *
 * A part added out of order used to mean deleting and re-adding every part
 * after it. Both editors of the list — the builder's (builder-core.js) and the
 * dashboard's window (admin.js openEventPartsEditor) — read their parts back
 * from the rows in DOCUMENT order, and every row carries its own
 * data-part-id, so moving a row is the whole operation and each guest's
 * answer stays with its part.
 *
 * Grip-only, like the question cards (builder-custom-fields.js
 * attachCustomFieldDrag): a row is draggable only while its grip is pressed,
 * so text in its boxes can still be selected. Arrow Up / Down on a focused
 * grip moves the row as well. Listeners are delegated on the container, since
 * rows come and go. The styles are injected here because the dashboard does
 * not load builder.css.
 *
 *   EventPartReorder.gripHtml(label)        the handle, first in a row's top line
 *   EventPartReorder.gutterHtml(inner)      the same width on the lines below it,
 *                                          so every box starts where the name does
 *   EventPartReorder.wire(container, onMove) once per container
 */
(function () {
    const ROW = '.event-part-edit';
    const GRIP = '[data-event-part-drag]';

    function injectStyles() {
        if (document.getElementById('event-part-reorder-styles')) return;
        const st = document.createElement('style');
        st.id = 'event-part-reorder-styles';
        st.textContent = [
            '.event-part-drag-handle{flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;',
            'align-self:stretch;min-width:28px;margin-inline-start:-.25rem;color:#9aa0a6;cursor:grab;touch-action:none;',
            'font-size:1.05rem;line-height:1;border-radius:4px}',
            '.event-part-drag-handle:hover{color:#16243F}.event-part-drag-handle:active{cursor:grabbing}',
            // Task #969: the lines under the name keep the handle's column, so
            // When and the tags start at the name's left edge.
            '.event-part-gutter{flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;',
            'min-width:28px;margin-inline-start:-.25rem;color:#9aa0a6}',
            // Pseudo-elements, as on the question cards: the row's own box-shadow
            // and outline are contested by other sheets on the builder.
            '.event-part-edit{position:relative}',
            '.event-part-edit.ep-drag-source::after{content:"";position:absolute;inset:0;background:rgba(255,255,255,.55);',
            'border-radius:inherit;z-index:5;pointer-events:none}',
            '.event-part-edit.ep-drop-before::after,.event-part-edit.ep-drop-after::after{content:"";position:absolute;',
            'left:0;right:0;height:4px;background:#1C1C1C;z-index:4;pointer-events:none}',
            '.event-part-edit.ep-drop-before::after{top:0}.event-part-edit.ep-drop-after::after{bottom:0}',
            // A phone (2026-10-30): the name keeps its whole line, and its
            // price, limit and delete wrap under it, under the name's edge -
            // four boxes on one 375px line left the name three letters wide.
            '@media (max-width:575.98px){.event-part-edit>.d-flex:first-child{flex-wrap:wrap}',
            '.event-part-edit .event-part-name-input,.event-part-edit .ep-name{flex:1 1 calc(100% - 40px)}',
            '.event-part-edit .event-part-price-group,.event-part-edit .input-group:has(.ep-price){margin-inline-start:calc(28px + .25rem)}}'
        ].join('');
        document.head.appendChild(st);
    }

    function gripHtml(label) {
        const esc = String(label || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
        return `<span class="event-part-drag-handle" data-event-part-drag tabindex="0" role="button" title="${esc}" aria-label="${esc}"><i class="bi bi-grip-vertical"></i></span>`;
    }

    function wire(rows, onMove) {
        if (!rows || rows.dataset.partReorder === '1') return;
        rows.dataset.partReorder = '1';
        injectStyles();
        const moved = () => { if (typeof onMove === 'function') onMove(); };
        const list = () => [...rows.querySelectorAll(ROW)];
        const clearMarks = () => rows.querySelectorAll('.ep-drop-before, .ep-drop-after')
            .forEach((r) => r.classList.remove('ep-drop-before', 'ep-drop-after'));
        const arm = (e) => {
            const grip = e.target.closest && e.target.closest(GRIP);
            if (grip) grip.closest(ROW).draggable = true;
        };
        const disarm = (e) => {
            const row = e.target.closest && e.target.closest(ROW);
            if (row && !row.classList.contains('ep-drag-source')) row.draggable = false;
        };
        rows.addEventListener('mousedown', arm);
        rows.addEventListener('touchstart', arm, { passive: true });
        rows.addEventListener('mouseup', disarm);
        rows.addEventListener('touchend', disarm);
        rows.addEventListener('dragstart', (e) => {
            const row = e.target.closest && e.target.closest(ROW);
            if (!row || !row.draggable || e.target.closest('input, textarea, button')) { e.preventDefault(); return; }
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', row.getAttribute('data-part-id') || '');
            row.classList.add('ep-drag-source');
        });
        rows.addEventListener('dragend', () => {
            list().forEach((r) => { r.draggable = false; r.classList.remove('ep-drag-source'); });
            clearMarks();
        });
        rows.addEventListener('dragover', (e) => {
            const source = rows.querySelector('.ep-drag-source');
            const row = e.target.closest && e.target.closest(ROW);
            if (!source || !row || row === source) return;
            e.preventDefault();                   // the default is to refuse a drop
            const rect = row.getBoundingClientRect();
            const after = (e.clientY - rect.top) / rect.height > 0.5;
            clearMarks();
            row.classList.add(after ? 'ep-drop-after' : 'ep-drop-before');
        });
        rows.addEventListener('drop', (e) => {
            const source = rows.querySelector('.ep-drag-source');
            const row = e.target.closest && e.target.closest(ROW);
            if (!source || !row) return;
            e.preventDefault();
            const after = row.classList.contains('ep-drop-after');
            clearMarks();
            if (row === source) return;
            row.parentNode.insertBefore(source, after ? row.nextSibling : row);
            moved();
        });
        rows.addEventListener('keydown', (e) => {
            const grip = e.target.closest && e.target.closest(GRIP);
            if (!grip || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
            e.preventDefault();
            const row = grip.closest(ROW);
            const all = list();
            const i = all.indexOf(row);
            const j = e.key === 'ArrowUp' ? i - 1 : i + 1;
            if (i < 0 || j < 0 || j >= all.length) return;
            if (e.key === 'ArrowUp') all[j].before(row); else all[j].after(row);
            moved();
            grip.focus();
        });
    }

    // Its styles are the grip's, which a page only has once wire() ran; a
    // row can be drawn first, so drawing one puts them in too.
    function gutterHtml(inner) {
        injectStyles();
        return `<span class="event-part-gutter">${inner || ''}</span>`;
    }

    window.EventPartReorder = { gripHtml, gutterHtml, wire };
})();
